import * as React from 'react';

import {
  Button,
  Col,
  NumberField,
  Row,
  Switch,
  TextField,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtom, useAtomValue } from 'jotai';

import {
  $createWarehouseCredential,
  $remoteWarehouseCredential,
  $updateWarehouseCredential,
  $warehouseCredential,
} from '../../../_state/organizationEntitlements.state';
import { useTranslations } from '../../../i18n/useTranslations';

//
// ⚛️ ⚛️ ⚛️ REPORTS ENTITLEMENT PANEL ⚛️ ⚛️ ⚛️
//
export const ReportsEntitlementPanel = () => {
  const t = useTranslations();
  const toast = useToast();

  const [enabled, setEnabled] = React.useState(false);
  const [databaseName, setDatabaseName] = React.useState('');
  const [username, setUsername] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [host, setHost] = React.useState('');
  const [port, setPort] = React.useState(5432);
  const [ssl, setSsl] = React.useState(true);

  const credential = useAtomValue($warehouseCredential);
  const { isLoading } = useAtomValue($remoteWarehouseCredential);
  const [createStatus, createCredential] = useAtom($createWarehouseCredential);
  const [updateStatus, updateCredential] = useAtom($updateWarehouseCredential);

  const isSaving = createStatus.isPending || updateStatus.isPending;

  React.useEffect(() => {
    if (credential) {
      setEnabled(credential.enabled);
      setDatabaseName(credential.databaseName);
      setUsername(credential.username);
      setHost(credential.host);
      setPort(credential.port);
      setSsl(credential.ssl);
      setPassword('');
    }
  }, [credential]);

  const handleSave = React.useCallback(async () => {
    try {
      if (credential) {
        await updateCredential({
          id: credential.id,
          input: {
            databaseName,
            username,
            host,
            port,
            ssl,
            enabled,
          },
          password: password || undefined,
        });
        toast.success(t.entitlements.reports.saveSuccess);
      } else {
        await createCredential({
          input: {
            databaseName,
            username,
            host,
            port,
            ssl,
            enabled,
          },
          password,
        });
        toast.success(t.entitlements.reports.saveSuccess);
      }
    } catch {
      toast.error(t.entitlements.reports.saveError);
    }
  }, [
    credential,
    databaseName,
    username,
    password,
    host,
    port,
    ssl,
    enabled,
    createCredential,
    updateCredential,
    toast,
    t,
  ]);

  return (
    <Col className="@ReportsEntitlementPanel gap-6 p-6">
      <Row className="items-center justify-between">
        <h3 className="text-lg font-medium">{t.entitlements.reports.title}</h3>
        <Switch isSelected={enabled} onChange={setEnabled}>
          {t.entitlements.reports.enabled}
        </Switch>
      </Row>

      <Col className="gap-4">
        <TextField
          label={t.entitlements.reports.fields.databaseName}
          value={databaseName}
          onChange={setDatabaseName}
          isDisabled={!enabled || isLoading}
        />
        <TextField
          label={t.entitlements.reports.fields.username}
          value={username}
          onChange={setUsername}
          isDisabled={!enabled || isLoading}
        />
        <TextField
          label={t.entitlements.reports.fields.password}
          type="password"
          value={password}
          onChange={setPassword}
          isDisabled={!enabled || isLoading}
          placeholder={
            credential
              ? t.entitlements.reports.fields.passwordPlaceholder
              : undefined
          }
        />
        <TextField
          label={t.entitlements.reports.fields.host}
          value={host}
          onChange={setHost}
          isDisabled={!enabled || isLoading}
        />
        <NumberField
          label={t.entitlements.reports.fields.port}
          value={port}
          onChange={setPort}
          isDisabled={!enabled || isLoading}
          minValue={1}
          maxValue={65535}
          showSteppers={false}
        />
        <Switch
          isSelected={ssl}
          onChange={setSsl}
          isDisabled={!enabled || isLoading}
        >
          {t.entitlements.reports.fields.ssl}
        </Switch>
      </Col>

      <Row>
        <Button
          onPress={handleSave}
          isDisabled={!enabled || isSaving || isLoading}
        >
          {isSaving
            ? t.entitlements.reports.saving
            : t.entitlements.reports.save}
        </Button>
      </Row>
    </Col>
  );
};
